import * as turf from '@turf/turf';
import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Collapse, Slider, Tooltip } from 'antd';
import { faCheck, faChevronLeft, faChevronRight, faCompressAlt, faHashtag, faPlus, faRotate, faRulerHorizontal, faSave, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';


//APP
import { gerarGrid } from '../../../meta';
import { errorAlert, loadingAlert } from '../../../../../../../../../../../../../../../../../utils/alert';

//COMPONENTS
import Input from '../../../../../../../../../../../../../../../../../Components/CustomUi/input';
import ButtonCustom from '../../../../../../../../../../../../../../../../../Components/ButtonCustom';
import { Box } from '../../../../../../../../../../../../../../../../../Components/CustomUi/box';
import { Inline } from '../../../../../../../../../../../../../../../../../Components/CustomUi/inline';
import { Render } from '../../../../../../../../../../../../../../../../../Components/CustomUi/render';
import { Warning } from '../../../../../../../../../../../../../../../../../Components/CustomUi/warning';
import { Switcher } from '../../../../../../../../../../../../../../../../../Components/CustomUi/Switcher';
import { GridMalha } from '../../gerarMalha';
import { UploadFileCustom } from '../../../../../../../../../../../../../../../../../Components/CustomUi/uploadFileCustom';
import { ModalPopOver } from '../../../../../../../../../../../../../../../../../Components/CustomUi/modalPopOver';


export interface PreviaReducaoTrilhas {
    total: number
    aposReducao: number
    removidos: number
    porcentagem: number
}

export interface CorrecaoSobreposicao {
    /** Contorno corrigido — igual ao original quando não foi possível corrigir */
    coords: { lat: number; lng: number }[]
    /** Se a correção zerou as sobreposições */
    resolvido: boolean
    kinksAntes: number
    kinksDepois: number
    /** Vértices com coordenadas iguais existentes antes da correção */
    duplicadosAntes: number
    /** Quantos vértices repetidos a correção removeu (etapa 1) */
    duplicadosRemovidos: number
    /**
     * Se a etapa 2 (unkinkPolygon) precisou rodar. Só nesse caso o formato do
     * contorno muda — remover vértices repetidos não move nenhum vértice.
     */
    formatoAlterado: boolean
    areaAntes: number
    areaDepois: number
    /** Variação de área em % — o número que indica o quanto o contorno mudou */
    variacaoArea: number
    /** Área descartada ao manter apenas a maior parte, em % do total */
    descartado: number
    /** Correção possível, mas fora do limite seguro de variação de área */
    arriscado: boolean
}

/**
 * Resultado da busca por uma simplificação que viabilize a edição do contorno.
 *
 * Contornos gravados por trator chegam com milhares de vértices, e o `editable`
 * do Google Maps cria dois overlays arrastáveis por vértice — é isso que travava
 * o app ao ligar a edição. A preparação reduz os trechos retos antes de entrar
 * no modo.
 */
export interface PreparacaoEdicao {
    /** Vértices do contorno atual */
    total: number
    /** Contorno simplificado — igual ao original quando não foi possível reduzir */
    coords: { lat: number; lng: number }[]
    /** Vértices após a simplificação */
    aposReducao: number
    /** Tolerância, em metros, que produziu esse resultado */
    tolerancia: number
    /** Se o resultado ficou dentro do limite que a edição suporta */
    dentroDoLimite: boolean
    /** Variação de área em % — o quanto a simplificação alterou o contorno */
    variacaoArea: number
}

/**
 * Onde desenhar os popovers. Diferente do módulo de grid, aqui o painel vive
 * dentro de um Modal centralizado, então a posição não pode ser fixa — ela é
 * medida a partir do painel em runtime.
 */
export interface PosicaoPopOver {
    top: number
    left: number
}

// Acima desta variação de área a correção é considerada arriscada (espelha o pai)
const LIMITE_VARIACAO_AREA = 5

/** Legenda das cores desenhadas no mapa durante a prévia */
const LegendaDaPreviaNoMapa: React.FC<{ rotuloVertices?: string }> = ({
    rotuloVertices = 'Vértices que serão removidos',
}) => (
    <Box gap={6}>
        <label style={{ fontSize: 11, color: '#7b7b7b', fontWeight: 600 }}>
            Prévia no mapa
        </label>
        <Inline alignItems='center' gap={8}>
            <span style={{ width: 18, height: 3, background: '#00e676', borderRadius: 2 }} />
            <label style={{ fontSize: 11, color: '#7b7b7b' }}>Contorno resultante</label>
        </Inline>
        <Inline alignItems='center' gap={8}>
            <span style={{ width: 10, height: 10, background: '#ff9100', borderRadius: '50%' }} />
            <label style={{ fontSize: 11, color: '#7b7b7b' }}>{rotuloVertices}</label>
        </Inline>
    </Box>
)

/** Números da prévia + legenda das cores desenhadas no mapa */
const ResumoDaPrevia: React.FC<{ previa: PreviaReducaoTrilhas }> = ({ previa }) => (
    <>
        <Box gap={8}>
            <Inline justifyContent='space-between'>
                <label style={{ fontSize: 12, color: '#7b7b7b' }}>Total de vértices:</label>
                <strong style={{ fontSize: 13, color: '#505050' }}>{previa.total}</strong>
            </Inline>

            <Inline justifyContent='space-between'>
                <label style={{ fontSize: 12, color: '#7b7b7b' }}>Vértices após a redução:</label>
                <strong style={{ fontSize: 13, color: '#2b8a3e' }}>{previa.aposReducao}</strong>
            </Inline>

            <Inline justifyContent='space-between'>
                <label style={{ fontSize: 12, color: '#7b7b7b' }}>Porcentagem de redução:</label>
                <strong style={{ fontSize: 13, color: '#c94a4a' }}>
                    {previa.porcentagem.toFixed(1)}%
                </strong>
            </Inline>
        </Box>

        <LegendaDaPreviaNoMapa />
    </>
)

interface PropsMapa {
    aoCarregarArquivo: (rawData: string, base64Data: string) => void
    validoFile: boolean
    rotation: number
    setRotation: React.Dispatch<React.SetStateAction<number>>
    rotacaoInput: string
    setRotacaoInput: React.Dispatch<React.SetStateAction<string>>
    gridLevel: string
    setGridLevel: React.Dispatch<React.SetStateAction<string>>
    position: { x: 0; y: 0 }
    setPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>
    movendo: boolean
    setMovendo: React.Dispatch<React.SetStateAction<boolean>>
    loading: string
    setLoading: React.Dispatch<React.SetStateAction<string>>
    editandoKml: boolean
    setGrid: React.Dispatch<React.SetStateAction<GridMalha[]>>
    setGridOriginal: React.Dispatch<React.SetStateAction<GridMalha[]>>
    setGridOriginalRotacao: React.Dispatch<React.SetStateAction<GridMalha[]>>
    setTamanhoGridOriginal: React.Dispatch<React.SetStateAction<number>>
    setQuadrante: React.Dispatch<React.SetStateAction<number>>
    areaHectares: number
    polygon: turf.Feature
    grid: GridMalha[]
    area: any[]
    kmlComCruzamento: boolean
    salvarContorno: () => void
    openCorrigirKinks: boolean
    setOpenCorrigirKinks: React.Dispatch<React.SetStateAction<boolean>>
    correcaoSobreposicoes: CorrecaoSobreposicao
    temAlgoParaCorrigir: boolean
    aplicarCorrecaoSobreposicoes: () => void
    openReducaoTrilhas: boolean
    setOpenReducaoTrilhas: React.Dispatch<React.SetStateAction<boolean>>
    distanciaTrilhas: number
    setDistanciaTrilhas: React.Dispatch<React.SetStateAction<number>>
    previaReducaoTrilhas: PreviaReducaoTrilhas
    aplicarReducaoTrilhas: () => void
    openReducaoRetas: boolean
    setOpenReducaoRetas: React.Dispatch<React.SetStateAction<boolean>>
    toleranciaRetas: number
    setToleranciaRetas: React.Dispatch<React.SetStateAction<number>>
    previaReducaoRetas: PreviaReducaoTrilhas
    aplicarReducaoRetas: () => void
    posicaoPopOver: PosicaoPopOver
    /** Liga/desliga a edição — intercepta a entrada quando o contorno é pesado */
    alternarEdicaoKml: () => void
    openPrepararEdicao: boolean
    setOpenPrepararEdicao: React.Dispatch<React.SetStateAction<boolean>>
    /** Nulo enquanto o popover de preparação está fechado — o cálculo é sob demanda */
    preparacaoEdicao: PreparacaoEdicao | null
    limiteVerticesEdicao: number
    totalVerticesContorno: number
    aplicarPreparacaoEdicao: () => void
    editarSemSimplificar: () => void
}

export const AbaMapa: React.FC<PropsMapa> = React.memo(({
    aoCarregarArquivo,
    validoFile,
    rotation,
    setRotation,
    rotacaoInput,
    setRotacaoInput,
    gridLevel,
    setGridLevel,
    position,
    setPosition,
    movendo,
    setMovendo,
    loading,
    setLoading,
    setGrid,
    setGridOriginal,
    setGridOriginalRotacao,
    setTamanhoGridOriginal,
    setQuadrante,
    areaHectares,
    editandoKml,
    polygon,
    area,
    grid,
    kmlComCruzamento,
    salvarContorno,
    openCorrigirKinks,
    setOpenCorrigirKinks,
    correcaoSobreposicoes,
    temAlgoParaCorrigir,
    aplicarCorrecaoSobreposicoes,
    openReducaoTrilhas,
    setOpenReducaoTrilhas,
    distanciaTrilhas,
    setDistanciaTrilhas,
    previaReducaoTrilhas,
    aplicarReducaoTrilhas,
    openReducaoRetas,
    setOpenReducaoRetas,
    toleranciaRetas,
    setToleranciaRetas,
    previaReducaoRetas,
    aplicarReducaoRetas,
    posicaoPopOver,
    alternarEdicaoKml,
    openPrepararEdicao,
    setOpenPrepararEdicao,
    preparacaoEdicao,
    limiteVerticesEdicao,
    totalVerticesContorno,
    aplicarPreparacaoEdicao,
    editarSemSimplificar,
}) => {
    const { Panel } = Collapse

    function adicionar(valor) {
        setMovendo(true)
        setRotation(valor + 1)
        setRotacaoInput(String(valor + 1))
    }

    function diminuir(valor) {
        setMovendo(true)
        setRotation(valor - 1)
        setRotacaoInput(String(valor - 1))
    }

    const valorDaRotacao = (e) => {
        const entrada = e

        // Permitir: vazio, "-" (usuário começando digitação), número válido
        if (entrada === '' || entrada === '-' || /^-?\d*\.?\d*$/.test(entrada)) {
            const num = parseFloat(entrada)

            if (isNaN(num)) {
                setRotacaoInput(entrada)
            } else {
                if (num >= -180 && num <= 180) {
                    setRotacaoInput(entrada)
                } else {
                    errorAlert('Limite excedido de rotação! limite é de -180 a 180')
                }
            }
        }
    }

    function tranformarEmNumber(valor: string): number | null {
        const normalizado = valor
            .trim()
            .replace(',', '.')
            .replace(/[^\d.-]/g, '')

        const numeroValido = /^-?\d+(\.\d+)?$/.test(normalizado)
        if (!numeroValido) return null

        const numero = Number(normalizado)
        return isNaN(numero) ? null : numero
    }

    function esconderMalha(cod) {
        const aba = cod[0]
        if (aba !== undefined) {
            setMovendo(true)
        }
    }

    function handleChange(valor) {
        let entrada = valor;

        // Substitui vírgula por ponto
        entrada = entrada.replace(",", ".");

        // Permite apenas números e ponto decimal (uma vez)
        if (/^\d*\.?\d*$/.test(entrada)) {
            setGridLevel(entrada);
        }
    }

    useEffect(() => {
        if (rotacaoInput !== null) {
            const numero = tranformarEmNumber(rotacaoInput)
            setRotation(numero === null ? 0 : numero)
        }

        if (rotacaoInput === null) {
            setRotation(0)
        }

    }, [rotacaoInput])

    useEffect(() => {
        if (movendo) {
            setRotacaoInput(String(rotation))
        }
    }, [rotation])

    useEffect(() => {
        if (loading === 'l') {
            loadingAlert({ msg: 'Gerando grid...', id: 'loading' })
            setTimeout(() => {
                setLoading('g')
            }, 300)
        }

        if (loading === 'g') {
            gerarGrid(
                Number(gridLevel),
                polygon,
                areaHectares,
                setGrid,
                setGridOriginal,
                setGridOriginalRotacao,
                setTamanhoGridOriginal,
                setLoading,
                setQuadrante
            )
        }
    }, [loading])

    const handleFile = (files: File[]) => {
        const file = files[0]
        if (!file) return

        if (!file.name.toLowerCase().endsWith('.kml')) {
            errorAlert('O arquivo deve ser um KML válido.')
            return
        }

        const reader = new FileReader()
        reader.onload = async (event) => {
            const content = event.target?.result as string

            const toBase64 = (file: File) => {
                return new Promise<string>((resolve, reject) => {
                    const readerBase64 = new FileReader()
                    readerBase64.readAsDataURL(file)
                    readerBase64.onload = () => resolve(readerBase64.result as string)
                    readerBase64.onerror = (error) => reject(error)
                })
            }

            const base64Data = await toBase64(file)
            const formattedBase64 = `data:@file/xml;base64,${base64Data.replace('data:', '').replace(/^.+,/, '')}`

            aoCarregarArquivo(content, formattedBase64)
        }
        reader.readAsText(file)
    }

    return (
        <>
            <UploadFileCustom
                label="Carregar KML"
                accept=".kml"
                onFileSelect={handleFile}
            />

            {/*
              * Corrigir as sobreposições zera `kmlComCruzamento` ainda dentro da
              * edição — o switch segue visível enquanto `editandoKml` estiver
              * ligado para o usuário conseguir sair do modo.
              */}
            <Render condition={kmlComCruzamento || editandoKml}>
                <Switcher
                    label="Editar contorno"
                    isChecked={editandoKml}
                    /**
                     * O `trigger` do Switcher espera um setState, mas aqui a
                     * entrada na edição pode ser interceptada pelo popover de
                     * preparação — por isso delegamos ao handler do pai em vez
                     * de inverter o booleano direto.
                     */
                    trigger={() => alternarEdicaoKml()}
                />
            </Render>

            <Render condition={editandoKml}>
                <Box>
                    <Warning style={{ fontSize: 11 }} type='warning'>
                        - Arraste os vértices para corrigir. <br />
                        - Botão direito do mouse exclui um vértice.
                    </Warning>
                    {/*
                      * O escopo fica em aviso próprio: "Salvar contorno" só confirma a
                      * edição aqui dentro — o contorno vai ao backend junto com a O.S.,
                      * como `arquivo_kml` no payload, sem tocar no cadastro do talhão.
                      */}
                    <Warning style={{ fontSize: 11 }} type='info'>
                        A edição do contorno vale apenas para esta OS, e é gravado ao salvá-la.<br />
                    </Warning>
                    <div>
                        <ButtonCustom
                            onClick={salvarContorno}
                            icon={<FontAwesomeIcon icon={faSave} />}
                            type='primary'
                        >
                            Salvar contorno
                        </ButtonCustom>
                    </div>
                </Box>

                <Box gap={8}>
                    <label style={{ fontSize: 12, color: '#7b7b7b', fontWeight: 600 }}>
                        Correção automática
                    </label>

                    <Tooltip placement='right' title={`Corrigir automaticamente linhas sobrepostas e vértices duplicados (${temAlgoParaCorrigir ? `encontrado ${correcaoSobreposicoes.kinksAntes} sobreposições e ${correcaoSobreposicoes.duplicadosAntes} vértices repetidos` : 'Nenhuma sobreposição ou vértice repetido nesse contorno'})`}><span>
                        <ButtonCustom
                            onClick={() => {
                                setOpenReducaoTrilhas(false)
                                setOpenReducaoRetas(false)
                                setOpenCorrigirKinks(!openCorrigirKinks)
                            }}
                            type={openCorrigirKinks ? 'primary' : 'secondary'}
                            icon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
                            disabled={!temAlgoParaCorrigir}
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            Corrigir automaticamente
                        </ButtonCustom>
                    </span></Tooltip>

                    <Tooltip placement='right' title='Reduzir a quantidade de vértices do contorno por distância'><span>
                        <ButtonCustom
                            onClick={() => {
                                setOpenReducaoRetas(false)
                                setOpenCorrigirKinks(false)
                                setOpenReducaoTrilhas(!openReducaoTrilhas)
                            }}
                            type={openReducaoTrilhas ? 'primary' : 'secondary'}
                            icon={<FontAwesomeIcon icon={faCompressAlt} />}
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            Redutor de trilhas
                        </ButtonCustom>
                    </span></Tooltip>

                    <Tooltip placement='right' title='Simplificar trechos retos do contorno, preservando cantos e curvas'><span>
                        <ButtonCustom
                            onClick={() => {
                                setOpenReducaoTrilhas(false)
                                setOpenCorrigirKinks(false)
                                setOpenReducaoRetas(!openReducaoRetas)
                            }}
                            type={openReducaoRetas ? 'primary' : 'secondary'}
                            icon={<FontAwesomeIcon icon={faRulerHorizontal} />}
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            Reduzir trilhas retas
                        </ButtonCustom>
                    </span></Tooltip>
                </Box>
            </Render>

            <ModalPopOver
                title='Corrigir automaticamente'
                open={openCorrigirKinks}
                onClose={() => setOpenCorrigirKinks(false)}
                width={330}
                height='auto'
                top={posicaoPopOver.top}
                left={posicaoPopOver.left}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                    {correcaoSobreposicoes.formatoAlterado ? (
                        <Warning style={{ fontSize: 11 }} type='warning'>
                            Remover os vértices repetidos não resolveu tudo, então o contorno também será
                            ajustado. Como o formato muda, revise o contorno e a área antes de salvar.
                        </Warning>
                    ) : (
                        <Warning style={{ fontSize: 11 }} type='info'>
                            Remove os vértices que estão na mesma coordenada. Não altera o formato nem a área
                            do contorno.
                        </Warning>
                    )}

                    <Box gap={8}>
                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Vértices repetidos:</label>
                            <strong
                                style={{
                                    fontSize: 13,
                                    color: correcaoSobreposicoes.duplicadosAntes > 0 ? '#c94a4a' : '#505050',
                                }}
                            >
                                {correcaoSobreposicoes.duplicadosAntes}
                            </strong>
                        </Inline>

                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Sobreposições:</label>
                            <strong
                                style={{
                                    fontSize: 13,
                                    color: correcaoSobreposicoes.kinksAntes > 0 ? '#c94a4a' : '#505050',
                                }}
                            >
                                {correcaoSobreposicoes.kinksAntes}
                            </strong>
                        </Inline>

                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Após a correção:</label>
                            <strong
                                style={{
                                    fontSize: 13,
                                    color: correcaoSobreposicoes.kinksDepois === 0 ? '#2b8a3e' : '#c94a4a',
                                }}
                            >
                                {correcaoSobreposicoes.kinksDepois} sobreposição(ões)
                            </strong>
                        </Inline>
                    </Box>

                    <Box gap={8}>
                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Área atual:</label>
                            <strong style={{ fontSize: 13, color: '#505050' }}>
                                {correcaoSobreposicoes.areaAntes.toFixed(2)} ha
                            </strong>
                        </Inline>

                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Área corrigida:</label>
                            <strong style={{ fontSize: 13, color: '#2b8a3e' }}>
                                {correcaoSobreposicoes.areaDepois.toFixed(2)} ha
                            </strong>
                        </Inline>

                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Variação da área:</label>
                            <strong
                                style={{
                                    fontSize: 13,
                                    color: correcaoSobreposicoes.arriscado ? '#c94a4a' : '#505050',
                                }}
                            >
                                {correcaoSobreposicoes.variacaoArea.toFixed(2)}%
                            </strong>
                        </Inline>
                    </Box>

                    <Render condition={correcaoSobreposicoes.arriscado}>
                        <Warning style={{ fontSize: 11 }} type='error'>
                            A correção mudaria {correcaoSobreposicoes.variacaoArea.toFixed(1)}% da área — acima
                            do limite seguro de {LIMITE_VARIACAO_AREA}%. Provavelmente o contorno tem um
                            problema que precisa de ajuste manual.
                        </Warning>
                    </Render>

                    <Render condition={!correcaoSobreposicoes.arriscado && !correcaoSobreposicoes.resolvido}>
                        <Warning style={{ fontSize: 11 }} type='error'>
                            Não foi possível resolver todas as sobreposições automaticamente. Restariam{' '}
                            {correcaoSobreposicoes.kinksDepois} — arraste os vértices para separar as linhas
                            sobrepostas.
                        </Warning>
                    </Render>

                    <LegendaDaPreviaNoMapa rotuloVertices='Vértices alterados pela correção' />

                    <ButtonCustom
                        onClick={() => aplicarCorrecaoSobreposicoes()}
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        disabled={!temAlgoParaCorrigir || correcaoSobreposicoes.arriscado}
                        title={
                            temAlgoParaCorrigir && !correcaoSobreposicoes.arriscado
                                ? 'Aplicar a correção no contorno'
                                : 'Correção automática indisponível para este contorno'
                        }
                        style={{ width: '100%', justifyContent: 'center' }}
                    >
                        Confirmar correção
                    </ButtonCustom>

                </div>
            </ModalPopOver>

            {/*
              * Preparação da edição — intercepta o switch quando o contorno tem
              * vértices demais para o `editable` do Google Maps aguentar.
              */}
            <ModalPopOver
                title='Preparar edição do contorno'
                open={openPrepararEdicao}
                onClose={() => setOpenPrepararEdicao(false)}
                width={330}
                height='auto'
                top={posicaoPopOver.top}
                left={posicaoPopOver.left}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                    <Warning style={{ fontSize: 11 }} type='warning'>
                        Este contorno tem {(preparacaoEdicao?.total ?? totalVerticesContorno).toLocaleString('pt-BR')}{' '}
                        vértices. A edição cria dois pontos de arraste por vértice, e acima de{' '}
                        {limiteVerticesEdicao.toLocaleString('pt-BR')} isso deixa o mapa travado.
                        Simplificar os trechos retos antes resolve sem perder o formato do talhão.
                    </Warning>

                    <Render condition={!!preparacaoEdicao && preparacaoEdicao.aposReducao < preparacaoEdicao.total}>
                        <>
                            <Box gap={8}>
                                <Inline justifyContent='space-between'>
                                    <label style={{ fontSize: 12, color: '#7b7b7b' }}>Vértices atuais:</label>
                                    <strong style={{ fontSize: 13, color: '#505050' }}>
                                        {preparacaoEdicao?.total.toLocaleString('pt-BR')}
                                    </strong>
                                </Inline>

                                <Inline justifyContent='space-between'>
                                    <label style={{ fontSize: 12, color: '#7b7b7b' }}>Após simplificar:</label>
                                    <strong style={{ fontSize: 13, color: '#2b8a3e' }}>
                                        {preparacaoEdicao?.aposReducao.toLocaleString('pt-BR')}
                                    </strong>
                                </Inline>

                                <Inline justifyContent='space-between'>
                                    <label style={{ fontSize: 12, color: '#7b7b7b' }}>Tolerância aplicada:</label>
                                    <strong style={{ fontSize: 13, color: '#505050' }}>
                                        {preparacaoEdicao?.tolerancia}m
                                    </strong>
                                </Inline>

                                <Inline justifyContent='space-between'>
                                    <label style={{ fontSize: 12, color: '#7b7b7b' }}>Variação de área:</label>
                                    <strong
                                        style={{
                                            fontSize: 13,
                                            color: (preparacaoEdicao?.variacaoArea ?? 0) > LIMITE_VARIACAO_AREA
                                                ? '#c94a4a'
                                                : '#2b8a3e',
                                        }}
                                    >
                                        {preparacaoEdicao?.variacaoArea.toFixed(2)}%
                                    </strong>
                                </Inline>
                            </Box>

                            {/*
                              * Legenda própria, sem o item de vértices removidos: esta
                              * prévia desenha só os dois contornos. Marcar milhares de
                              * vértices traria de volta o travamento que o fluxo evita.
                              */}
                            <Box gap={6}>
                                <label style={{ fontSize: 11, color: '#7b7b7b', fontWeight: 600 }}>
                                    Prévia no mapa
                                </label>
                                <Inline alignItems='center' gap={8}>
                                    <span style={{ width: 18, height: 3, background: '#00e676', borderRadius: 2 }} />
                                    <label style={{ fontSize: 11, color: '#7b7b7b' }}>Contorno simplificado</label>
                                </Inline>
                                <Inline alignItems='center' gap={8}>
                                    {/*
                                      * Mesma expressão do `strokeColor` do polígono no mapa:
                                      * vermelho com cruzamento, ciano quando limpo. Fixar uma
                                      * das duas apontaria para uma linha que não está na tela.
                                      * A espessura espelha o traço reforçado da preparação.
                                      */}
                                    <span style={{
                                        width: 18,
                                        height: 5,
                                        background: kmlComCruzamento ? '#ff4444' : 'cyan',
                                        borderRadius: 2,
                                    }} />
                                    <label style={{ fontSize: 11, color: '#7b7b7b' }}>Contorno atual</label>
                                </Inline>
                            </Box>

                            <Render condition={(preparacaoEdicao?.variacaoArea ?? 0) > LIMITE_VARIACAO_AREA}>
                                <Warning style={{ fontSize: 11 }} type='error'>
                                    A simplificação alteraria {preparacaoEdicao?.variacaoArea.toFixed(2)}% da área,
                                    acima do limite seguro de {LIMITE_VARIACAO_AREA}%. Confira a prévia no mapa
                                    antes de confirmar.
                                </Warning>
                            </Render>

                            <Render condition={!!preparacaoEdicao && !preparacaoEdicao.dentroDoLimite}>
                                <Warning style={{ fontSize: 11 }} type='warning'>
                                    Mesmo simplificado o contorno segue acima do limite. A edição vai funcionar,
                                    mas ainda pode ficar lenta.
                                </Warning>
                            </Render>
                        </>
                    </Render>

                    {/* Nenhuma tolerância da escala conseguiu remover vértices */}
                    <Render condition={!!preparacaoEdicao && preparacaoEdicao.aposReducao >= preparacaoEdicao.total}>
                        <Warning style={{ fontSize: 11 }} type='error'>
                            Não foi possível simplificar este contorno automaticamente — ele não tem trechos
                            retos redundantes. A edição vai ficar lenta.
                        </Warning>
                    </Render>

                    <ButtonCustom
                        onClick={() => aplicarPreparacaoEdicao()}
                        icon={<FontAwesomeIcon icon={faCompressAlt} />}
                        disabled={!preparacaoEdicao || preparacaoEdicao.aposReducao >= preparacaoEdicao.total}
                        title={
                            preparacaoEdicao && preparacaoEdicao.aposReducao < preparacaoEdicao.total
                                ? `${(preparacaoEdicao.total - preparacaoEdicao.aposReducao).toLocaleString('pt-BR')} vértice(s) serão removidos`
                                : 'Nenhum vértice seria removido neste contorno'
                        }
                        style={{ width: '100%', justifyContent: 'center' }}
                    >
                        Simplificar e editar
                    </ButtonCustom>

                    <ButtonCustom
                        onClick={() => editarSemSimplificar()}
                        type='secondary'
                        icon={<FontAwesomeIcon icon={faRulerHorizontal} />}
                        title='Mantém todos os vértices — o mapa vai ficar lento'
                        style={{ width: '100%', justifyContent: 'center' }}
                    >
                        Editar sem simplificar
                    </ButtonCustom>

                </div>
            </ModalPopOver>

            <ModalPopOver
                title='Redutor de trilhas'
                open={openReducaoTrilhas}
                onClose={() => setOpenReducaoTrilhas(false)}
                width={330}
                height='auto'
                top={posicaoPopOver.top}
                left={posicaoPopOver.left}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                    <Warning style={{ fontSize: 11 }} type='info'>
                        Vértices que estiverem a menos da distância informada do vértice anterior serão
                        removidos. Confira a prévia no mapa antes de confirmar.
                    </Warning>

                    <div>
                        <label style={{ fontSize: 12, color: '#7b7b7b', fontWeight: 600 }}>
                            Distância entre os vértices:{' '}
                            <strong style={{ color: '#505050', fontSize: 13 }}>{distanciaTrilhas}m</strong>
                        </label>
                        <Slider
                            min={1}
                            max={50}
                            step={1}
                            value={distanciaTrilhas}
                            onChange={(valor) => setDistanciaTrilhas(valor as number)}
                            marks={{ 1: '1m', 25: '25m', 50: '50m' }}
                        />
                    </div>

                    <ResumoDaPrevia previa={previaReducaoTrilhas} />

                    <ButtonCustom
                        onClick={() => aplicarReducaoTrilhas()}
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        disabled={previaReducaoTrilhas.removidos <= 0}
                        title={
                            previaReducaoTrilhas.removidos > 0
                                ? `${previaReducaoTrilhas.removidos} vértice(s) serão removidos`
                                : 'Nenhum vértice seria removido com esse intervalo'
                        }
                        style={{ width: '100%', justifyContent: 'center' }}
                    >
                        Confirmar redução
                    </ButtonCustom>

                </div>
            </ModalPopOver>

            <ModalPopOver
                title='Reduzir trilhas retas'
                open={openReducaoRetas}
                onClose={() => setOpenReducaoRetas(false)}
                width={330}
                height='auto'
                top={posicaoPopOver.top}
                left={posicaoPopOver.left}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                    <Warning style={{ fontSize: 11 }} type='info'>
                        Simplifica apenas os trechos retos: vértices que não alteram o formato da linha são
                        removidos, preservando cantos e curvas. Confira a prévia no mapa antes de confirmar.
                    </Warning>

                    <div>
                        <label style={{ fontSize: 12, color: '#7b7b7b', fontWeight: 600 }}>
                            Tolerância:{' '}
                            <strong style={{ color: '#505050', fontSize: 13 }}>{toleranciaRetas}m</strong>
                        </label>
                        <Slider
                            min={0.5}
                            max={5}
                            step={0.5}
                            value={toleranciaRetas}
                            onChange={(valor) => setToleranciaRetas(valor as number)}
                            marks={{ 0.5: '0,5m', 5: '5m' }}
                        />
                    </div>

                    <ResumoDaPrevia previa={previaReducaoRetas} />

                    <ButtonCustom
                        onClick={() => aplicarReducaoRetas()}
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        disabled={previaReducaoRetas.removidos <= 0}
                        title={
                            previaReducaoRetas.removidos > 0
                                ? `${previaReducaoRetas.removidos} vértice(s) serão removidos`
                                : 'Nenhum trecho reto seria simplificado com essa tolerância'
                        }
                        style={{ width: '100%', justifyContent: 'center' }}
                    >
                        Confirmar redução
                    </ButtonCustom>

                </div>
            </ModalPopOver>

            <Render condition={!editandoKml}>
                <Inline>
                    <Input
                        label='Tamanho do grid'
                        placeholder={''}
                        value={gridLevel}
                        onChange={handleChange}
                        icon={faHashtag}
                        desabilitado={area.length <= 0 || kmlComCruzamento}
                    />
                    <ButtonCustom
                        onClick={() => setLoading('l')}
                        style={{ marginTop: '30px' }}
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        disabled={area.length <= 0 || kmlComCruzamento}
                        loading={loading === 'l'}
                    >
                        Gerar
                    </ButtonCustom>
                </Inline>

                <div
                    onMouseEnter={() => setMovendo(true)}
                    onMouseLeave={() => setMovendo(false)}
                    style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                >

                    <Collapse accordion destroyInactivePanel onChange={esconderMalha}>
                        <Panel header={'Rotação'} key={1}>
                            <Input
                                placeholder={'Digite a rotação'}
                                value={rotacaoInput}
                                onChange={(e) => valorDaRotacao(e)}
                                max={180}
                                min={-180}
                                desabilitado={grid.length <= 0 || kmlComCruzamento}
                                icon={faRotate}
                            />
                            <Slider
                                min={-180}
                                max={180}
                                step={1}
                                value={rotation}
                                onChange={(e) => {
                                    setRotation(e)
                                    // começou a usar
                                }}
                                style={{ width: '98%' }}
                                disabled={grid.length <= 0 || kmlComCruzamento}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <div style={{ display: 'flex', gap: 5 }}>
                                    <ButtonCustom
                                        style={{
                                            width: '20px',
                                            height: '30px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                        onClick={() => diminuir(rotation)}
                                        icon={<FontAwesomeIcon icon={faChevronLeft} />}
                                        disabled={grid.length <= 0 || kmlComCruzamento}
                                    />
                                    <ButtonCustom
                                        style={{
                                            width: '20px',
                                            height: '30px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                        onClick={() => adicionar(rotation)}
                                        icon={<FontAwesomeIcon icon={faChevronRight} />}
                                        disabled={grid.length <= 0 || kmlComCruzamento}
                                    />
                                </div>
                            </div>
                        </Panel>
                        <Panel header={'Posicionar'} key={2}>
                            <div>
                                <span style={{ fontSize: '12px' }}>Horizontal:</span>
                                <Slider
                                    min={-360}
                                    max={360}
                                    step={1}
                                    value={position.x}
                                    onChange={(value) => {
                                        setPosition((prev) => ({ ...prev, x: value as number }))
                                    }}
                                    disabled={grid.length <= 0 || kmlComCruzamento}
                                />
                            </div>
                            <div>
                                <span style={{ fontSize: '12px' }}>Vertical:</span>
                                <Slider
                                    min={-360}
                                    max={360}
                                    step={1}
                                    value={position.y}
                                    onChange={(value) => {
                                        setPosition((prev) => ({ ...prev, y: value as number }))
                                    }}
                                    disabled={grid.length <= 0 || kmlComCruzamento}
                                />
                            </div>
                        </Panel>
                    </Collapse>
                    <Warning style={{ fontSize: 11 }} type='info'>
                        Ao rotacionar ou posicionar, a malha será temporariamente ocultada para otimizar a
                        visualização.
                    </Warning>
                </div>
            </Render>
        </>
    )
})